import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";

import { CoverageComparison } from "Components/CoverageComparison";
import { CoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/CoverageCard";

import InlineAlert from "@storeblocks/inline-alert";
import { StyledAlert } from "App/global-style";
import { CheckPriceSurveyAccordion } from "Components/Features/CheckPrice/CheckPriceSurvey";
import {
  InsuranceTypeVariantVariable,
  coveragePriceDifference,
} from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { Data } from "Components/TextContext/textInterfaces";
import { RentalType } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductDetails/FormElements/RentalType";
import { CarCoverageMissingReasons } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/FormElements/CoverageMissingReasons";
import {
  CarCoverage,
  InsuranceCoverage,
  RENTAL_CAR_COVERAGES,
} from "Store/interfaces/products/coverage";
import { getError } from "Utils/productUtils";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";
import { CarPriceFormValues } from "../interfaces";
import { CoverageRadioGroup } from "./styled";

interface Props {
  readonly coverages: CoveragePriceCalculation[];
  readonly coverageStandards: InsuranceCoverage[];
  readonly priceFilterValue?: string;
  readonly product: ProductBackendName;
  readonly priceIsLoading?: boolean;
  readonly textComponents: Data[];
  readonly alertLabel?: string;
  readonly canOnlyBuyKasko?: boolean;
  readonly disabledCoverages?: Record<string, React.ReactNode>;
  readonly doNotShow?: InsuranceType;
  readonly insuranceTypeVariant?: InsuranceTypeVariantVariable;
  readonly updatePrices: (field: string, value: unknown) => void;
}

export const CarProductCoverages = ({
  coverages,
  coverageStandards,
  priceFilterValue,
  product,
  priceIsLoading,
  textComponents,
  alertLabel,
  canOnlyBuyKasko,
  disabledCoverages = {},
  doNotShow,
  insuranceTypeVariant,
  updatePrices,
}: Props): React.ReactElement => {
  const formProps = useFormikContext<CarPriceFormValues>();

  const fieldName = "coverage";
  const selectedCoverage = formProps.values[fieldName];
  const filteredAndSortedCoverages = useSortedAndFilteredCarCoverages({
    coverages,
    coverageStandards,
  });

  return (
    <>
      <CoverageRadioGroup name={fieldName}>
        {getError(fieldName, formProps) && (
          <InlineAlert
            variant="error"
            title={formProps.errors[fieldName] as string}
          />
        )}
        {filteredAndSortedCoverages.map(([coverage, priceCalculationEntry]) => {
          const isCoverageWithRentalType =
            RENTAL_CAR_COVERAGES.includes(selectedCoverage);
          const isDisabledCoverage =
            Object.keys(disabledCoverages).includes(coverage);
          return (
            <CoverageCard
              key={coverage}
              fieldName={fieldName}
              product={product}
              textComponents={textComponents}
              coverage={coverage}
              prices={priceCalculationEntry.prices}
              priceFilterValue={priceFilterValue}
              hideRadioButton={isDisabledCoverage}
              hideDescription={
                isCoverageWithRentalType
                  ? !(coverage === selectedCoverage)
                  : true
              }
              disabled={isDisabledCoverage}
              disabledCoverageDesc={disabledCoverages[coverage] ?? undefined}
              priceIsLoading={priceIsLoading}
              doNotShow={doNotShow}
              insuranceTypeVariant={insuranceTypeVariant}
            >
              {isCoverageWithRentalType && coverage === selectedCoverage && (
                <RentalType
                  updatePrices={updatePrices}
                  coverage={coverage as CarCoverage}
                  priceIsLoading={priceIsLoading}
                />
              )}
            </CoverageCard>
          );
        })}
      </CoverageRadioGroup>

      {coverages.length > 0 && (
        <>
          <CarCoverageMissingReasons coverages={coverages} />
          <CoverageComparison
            productBackendName={product}
            selectedCoverage={selectedCoverage}
            doNotShow={doNotShow}
          />
        </>
      )}
      <CheckPriceSurveyAccordion />
      {canOnlyBuyKasko && coverages.length > 0 && alertLabel && (
        <StyledAlert title={alertLabel} />
      )}
    </>
  );
};

const useSortedAndFilteredCarCoverages = ({
  coverages,
  coverageStandards,
}: Pick<
  Props,
  "coverages" | "coverageStandards"
>): CoveragePriceCalculation[] => {
  return useMemo(
    () =>
      sortAndFilterCoverages({
        coverages,
        coverageStandards,
      }),
    [coverages, coverageStandards],
  );
};

const sortAndFilterCoverages = ({
  coverages,
  coverageStandards,
}: Pick<
  Props,
  "coverages" | "coverageStandards"
>): CoveragePriceCalculation[] => {
  const includesCoverage = ([coverage]: CoveragePriceCalculation): boolean =>
    coverageStandards.includes(coverage);

  return coverages
    .filter(includesCoverage)
    .filter((coverage) => !isCarCoverageAvailable(coverage))
    .sort(coveragePriceDifference);
};

const isCarCoverageAvailable = (
  coverage: CoveragePriceCalculation,
): boolean => {
  const reasons = coverage[1]?.notAvailableReasons ?? [];
  return reasons.length !== 0;
};
